import React from "react";
import styled from "styled-components";
import Squiggle from "./Squiggle";
import Connector from "./Connector";
import CtaButton from "./CtaButton";
import { ReactComponent as Feather } from "../../../../assets/landing/patria-feather.svg";

/**
 * The process section. Shared by both landers: v1 puts bone cards' inverse
 * (white cards on bone), v2 flips it (bone cards on white). Everything else —
 * copy, accents, connectors, squiggle — is identical, so the colours are props.
 *
 * See Hero.styles.ts for the px-not-rem note.
 */
const Wrapper = styled.section<{ $bg: string }>`
  position: relative;
  background: ${(props) => props.$bg};

  .inner {
    margin: 0 auto;
    max-width: 1152px;
    padding: 80px 20px;
  }

  .head {
    margin-bottom: 64px;
    text-align: center;
  }

  .eyebrow {
    margin: 0 0 12px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: rgba(14, 17, 22, 0.6);
  }

  .heading {
    margin: 0;
    font-family: var(--brand-display);
    font-size: 36px;
    font-weight: 800;
    letter-spacing: -0.025em;
    line-height: 1.1;
    color: var(--brand-ink);

    .accent {
      color: var(--brand-red);
    }
  }

  .underlined {
    position: relative;
    display: inline-block;

    > span {
      position: relative;
      z-index: 1;
    }

    svg.squiggle {
      position: absolute;
      left: 0;
      bottom: -8px;
      width: 100%;
      height: 10px;
      color: var(--brand-red);
    }
  }

  .steps {
    position: relative;
    display: grid;
    gap: 32px;
  }

  /* the two dashed curves linking the three cards on wider screens */
  .connector {
    pointer-events: none;
    display: none;
    position: absolute;
    top: 64px;
    height: 40px;
    width: 10%;
    color: rgba(14, 17, 22, 0.3);

    &.connector-1 {
      left: 30%;
    }
    &.connector-2 {
      left: 63%;
    }
  }

  .cta {
    margin-top: 56px;
    text-align: center;
  }

  @media screen and (min-width: 640px) {
    .inner {
      padding: 80px 32px;
    }

    .heading {
      font-size: 48px;
    }

    .steps {
      grid-template-columns: repeat(3, 1fr);
      gap: 24px;
    }

    .connector {
      display: block;
    }
  }

  @media screen and (min-width: 1024px) {
    .inner {
      padding: 96px 32px;
    }

    .heading {
      font-size: 56px;
    }

    .steps {
      gap: 40px;
    }
  }
`;

const StepCard = styled.div<{
  $accent: string;
  $rotate: string;
  $numColor: string;
  $cardBg: string;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: ${(props) => props.$cardBg};
  padding: 28px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.3s;

  /* Tailwind's shadow-xl is two layers; emitting only the first made the lift
   * read much weaker than the reference. */
  &:hover {
    transform: translateY(-6px);
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
  }

  .num-wrap {
    position: relative;
    margin-bottom: 28px;
  }

  /* the rotated colour swatch behind the step number, straightening on hover */
  .num-accent {
    position: absolute;
    inset: -8px;
    border-radius: 8px;
    background: ${(props) => props.$accent};
    transform: rotate(${(props) => props.$rotate});
    transition: transform 0.3s;
  }

  &:hover .num-accent {
    transform: rotate(0deg);
  }

  .num {
    position: relative;
    font-family: var(--brand-display);
    font-size: 36px;
    font-weight: 800;
    line-height: 1;
    color: ${(props) => props.$numColor};
  }

  .step-title {
    margin: 0;
    font-family: var(--brand-display);
    font-size: 20px;
    font-weight: 800;
    color: var(--brand-ink);
  }

  .step-text {
    margin: 12px 0 0;
    font-size: 16px;
    line-height: 1.625;
    color: rgba(14, 17, 22, 0.7);

    strong {
      font-weight: 600;
      color: var(--brand-ink);
    }

    em {
      border-radius: 2px;
      background: var(--brand-bone);
      padding: 0 4px;
      font-style: italic;
    }

    .ok {
      font-weight: 600;
      color: var(--brand-green-deep);
    }
  }

  .watermark {
    pointer-events: none;
    position: absolute;
    bottom: -8px;
    right: -8px;
    width: 80px;
    height: 80px;
    opacity: 0.06;
    color: var(--brand-ink);

    svg {
      width: 100%;
      height: 100%;
    }
  }

  @media screen and (min-width: 640px) {
    padding: 32px;

    .step-title {
      font-size: 24px;
    }
  }
`;

const steps = [
  {
    num: "01",
    title: "Tell us about you",
    accent: "rgba(63, 165, 255, 0.2)",
    rotate: "-6deg",
    numColor: "var(--brand-ink)",
    // only the first card carries the feather watermark
    withFeather: true,
    body: (
      <>
        Quick form. Basic info. <span className="ok">No credit hit.</span>
      </>
    ),
  },
  {
    num: "02",
    title: "Get your decision",
    accent: "rgba(209, 31, 61, 0.1)",
    rotate: "3deg",
    numColor: "var(--brand-red)",
    withFeather: false,
    body: (
      <>
        Real-time answer in <strong>seconds</strong> — <em>not days</em>.
      </>
    ),
  },
  {
    num: "03",
    title: "Money in your bank",
    accent: "rgba(34, 197, 143, 0.2)",
    rotate: "-3deg",
    numColor: "var(--brand-ink)",
    withFeather: false,
    body: (
      <>
        Deposits often land <strong>within minutes</strong>.
      </>
    ),
  },
];

const HowItWorks = ({
  background = "var(--brand-bone)",
  cardBackground = "#fff",
}: {
  background?: string;
  cardBackground?: string;
}) => {
  return (
    <Wrapper className="section-lp-how" $bg={background}>
      <div className="inner">
        <div className="head">
          <p className="eyebrow">The process</p>
          <h2 className="heading">
            How it works —{" "}
            <span className="underlined">
              <span>Three steps.</span>
              <Squiggle className="squiggle" />
            </span>{" "}
            <span className="accent">That&apos;s it.</span>
          </h2>
        </div>

        <div className="steps">
          <Connector variant={1} className="connector connector-1" />
          <Connector variant={2} className="connector connector-2" />

          {steps.map(
            ({ num, title, body, accent, rotate, numColor, withFeather }) => (
              <StepCard
                key={num}
                $accent={accent}
                $rotate={rotate}
                $numColor={numColor}
                $cardBg={cardBackground}
              >
                <div className="num-wrap">
                  <span className="num-accent" aria-hidden="true" />
                  <span className="num">{num}</span>
                </div>
                <h3 className="step-title">{title}</h3>
                <p className="step-text">{body}</p>
                {withFeather ? (
                  <span className="watermark" aria-hidden="true">
                    <Feather />
                  </span>
                ) : null}
              </StepCard>
            ),
          )}
        </div>

        <div className="cta">
          <CtaButton>Check your approval</CtaButton>
        </div>
      </div>
    </Wrapper>
  );
};

export default HowItWorks;
